import { useRef, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import RtcClient from '@/lib/RtcClient';
import { COMMAND, INTERRUPT_PRIORITY } from '@/utils/handler';
import { setInterruptMsg, setHistoryMsg } from '@/store/slices/room';
import store from '@/store';

import logger from '@/utils/logger';

export interface VoiceInteractionConfig {
  interruptTimeout?: number;
  waitAIStopTimeout?: number;
  delayAfterStop?: number;
}

export interface VoiceInteractionControls {
  sendMessage: (text: string) => Promise<void>;
  handleManualInterrupt: () => void;
  sendAutoQuestion: (question: string) => Promise<void>;
}

const DEFAULT_CONFIG: Required<VoiceInteractionConfig> = {
  interruptTimeout: 500,
  waitAIStopTimeout: 500,
  delayAfterStop: 200,
};

/**
 * 语音交互管理 Hook
 * 负责消息发送、AI打断、自动提问等语音交互逻辑
 */
export function useVoiceInteraction(config: VoiceInteractionConfig = {}): VoiceInteractionControls {
  const dispatch = useDispatch();
  const room = useSelector((state: any) => state.room);
  const { isAITalking, localUser } = room;

  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const hasAutoQuestionSent = useRef(false);

  // 等待AI停止说话
  const waitAIStop = useCallback((): Promise<void> => {
    return new Promise<void>((resolve) => {
      if (!store.getState().room.isAITalking) {
        resolve();
        return;
      }

      let unsubscribe: () => void = () => {};
      const timeout = setTimeout(() => {
        unsubscribe();
        resolve();
      }, finalConfig.waitAIStopTimeout);

      unsubscribe = store.subscribe(() => {
        if (!store.getState().room.isAITalking) {
          clearTimeout(timeout);
          unsubscribe();
          resolve();
        }
      });
    });
  }, [finalConfig.waitAIStopTimeout]);

  // 发送消息给AI
  const sendMessageToAI = useCallback(
    async (command: COMMAND, agentName: string, message?: string): Promise<void> => {
      try {
        await RtcClient.commandAgent({
          command,
          agentName,
          message,
          interruptMode: INTERRUPT_PRIORITY.HIGH,
        });
        logger.info('[VoiceInteraction] 消息发送成功');
      } catch (error) {
        logger.error('[VoiceInteraction] 发送消息失败:', error);
        throw error;
      }
    },
    []
  );

  // 发送打断指令
  const sendInterruptCommand = useCallback(async (agentName: string): Promise<void> => {
    try {
      await RtcClient.commandAgent({
        agentName,
        command: COMMAND.INTERRUPT,
      });
      logger.info('[VoiceInteraction] 打断指令发送成功');
    } catch (error) {
      logger.warn('[VoiceInteraction] 发送打断指令失败:', error);
      // 打断失败不应该阻止后续流程
    }
  }, []);

  // 简化的发送消息逻辑
  const sendMessage = useCallback(
    async (text: string): Promise<void> => {
      const trimmedText = text.trim();
      if (!trimmedText || !localUser?.userId) {
        logger.warn('[VoiceInteraction] 消息内容为空或用户信息不完整');
        return;
      }

      // 获取场景信息
      const { scene, sceneConfigMap } = store.getState().room;
      const botName = sceneConfigMap[scene]?.botName || 'default';

      try {
        logger.info('[VoiceInteraction] 发送消息:', trimmedText);

        // 如果AI正在说话，先打断
        if (isAITalking) {
          await sendInterruptCommand(botName);
          dispatch(setInterruptMsg());
          await waitAIStop();
        }

        // 延迟后发送消息
        await new Promise<void>((resolveDelay) => {
          setTimeout(resolveDelay, finalConfig.delayAfterStop);
        });

        // 先显示用户消息
        dispatch(
          setHistoryMsg({
            text,
            user: localUser.userId,
            paragraph: true,
          })
        );
        logger.info('[VoiceInteraction] 用户消息已添加到历史');

        // 然后发送给AI
        await sendMessageToAI(COMMAND.EXTERNAL_TEXT_TO_LLM, botName, text);

        logger.info('[VoiceInteraction] 消息发送流程完成');
      } catch (error) {
        logger.error('[VoiceInteraction] 发送消息过程中出错:', error);
        // 可以考虑添加错误提示给用户
      }
    },
    [
      isAITalking,
      localUser,
      dispatch,
      sendInterruptCommand,
      waitAIStop,
      sendMessageToAI,
      finalConfig.delayAfterStop,
    ]
  );

  // 手动打断AI说话
  const handleManualInterrupt = useCallback((): void => {
    const currentRoom = store.getState().room;
    const { scene, sceneConfigMap } = currentRoom;
    const sceneConfig = sceneConfigMap[scene];
    const botName = sceneConfig?.botName || 'default';

    logger.info('[VoiceInteraction] 手动打断AI');

    sendInterruptCommand(botName);
    dispatch(setInterruptMsg());
  }, [sendInterruptCommand, dispatch]);

  // 发送自动提问
  const sendAutoQuestion = useCallback(
    async (question: string): Promise<void> => {
      if (hasAutoQuestionSent.current) {
        logger.info('[VoiceInteraction] 自动提问已发送，跳过');
        return;
      }

      if (!question.trim()) {
        logger.warn('[VoiceInteraction] 自动提问内容为空');
        return;
      }

      try {
        logger.info('[VoiceInteraction] 发送自动提问:', question);
        await sendMessage(question);
        hasAutoQuestionSent.current = true;

        // 清除URL参数，避免重复发送
        const url = new URL(window.location.href);
        url.searchParams.delete('Question');
        url.searchParams.delete('DirectRoom'); // 也删除DirectRoom参数
        // 保留layout2 / layout3等其他重要参数
        window.history.replaceState({}, '', url.toString());

        logger.info('[VoiceInteraction] 自动提问发送完成，已清除URL参数');
      } catch (error) {
        logger.error('[VoiceInteraction] 发送自动提问失败:', error);
        // 重置标记，允许重试
        hasAutoQuestionSent.current = false;
      }
    },
    [sendMessage]
  );

  // 重置自动提问状态（用于场景切换等情况）
  const resetAutoQuestionState = useCallback((): void => {
    hasAutoQuestionSent.current = false;
    logger.info('[VoiceInteraction] 自动提问状态已重置');
  }, []);

  // 监听场景变化，重置自动提问状态
  useEffect(() => {
    const currentScene = room.scene;
    if (currentScene) {
      resetAutoQuestionState();
    }
  }, [room.scene, resetAutoQuestionState]);

  return useMemo(
    () => ({
      sendMessage,
      handleManualInterrupt,
      sendAutoQuestion,
    }),
    [sendMessage, handleManualInterrupt, sendAutoQuestion]
  );
}
